import {
  useAmendTradeSaleMutation,
  useAppService,
  useInvalidateSaleQueries,
} from "@fero/hooks";
import { useTradeSalesStore } from "@fero/stores";
import { AppRoutes } from "@fero/utils";
import type { AppService } from "adl-gen/app-service";
import type { CreatePurchaseRequestResp } from "adl-gen/ferovinum/app/api";
import { useUiConfig } from "adl-service/hooks/use-ui-config";
import { useAlert } from "components/context/global-alert/use-alert-context";
import { Loader } from "components/widgets/loader/loader";
import hash from "object-hash";
import { useCallback, useContext } from "react";
import { Redirect, useHistory, useLocation } from "react-router-dom";
import { UploadFile } from "utils/file-fetch-utils";
import { assertNotUndefined } from "utils/hx/util/types";
import { ensure } from "utils/null-and-undefined-utils";
import { isLoaded } from "utils/utility-types";
import { LoggedInContext } from "../../../../../app/app";
import type { PurchaseRequestFlowState } from "../purchase-request-types";
import { usePurchaseRequestPreview } from "../use-purchase-request-preview";
import { OrganisationPurchaseRequestConfirmationPageView } from "./organisation-purchase-request-confirmation-page-view";

const getErrorMessage = (resp: CreatePurchaseRequestResp): string => {
  switch (resp.kind) {
    case "noLineItems":
      return "Does not contain any valid line items";
    case "invalidQuantity":
      return "Invalid quantities for the selected line items";
    case "insufficientCredit":
      return "Insufficient credit";
    case "invalidCashAdvance":
      return "Unable to advance cash upon delivery";
    case "negativeReceivableValue":
      return "Invalid net receivable amount";
    case "invalidProductionOrder":
      return "Invalid production order";
    case "error":
      return resp.value;
    default:
      return "Unknown error";
  }
};

const uploadFile = async (
  isDev: boolean,
  service: AppService,
  file: File,
  token: string,
): Promise<string> => {
  const fileUploadResp = await UploadFile(isDev, service, file, token);
  if (fileUploadResp.kind === "success") {
    return fileUploadResp.fileId ?? null;
  }
  throw new Error(`Unable to upload file: ${file.name}`);
};

export const OrganisationPurchaseRequestConfirmationPage = () => {
  const isDevelopment = useUiConfig().environment === "dev";
  const authToken: string = assertNotUndefined(
    useContext(LoggedInContext).loginState?.token,
  );
  const service = useAppService();
  const location = useLocation();
  const history = useHistory();
  const [showAlert] = useAlert();
  const purchaseRequestFlowState = ensure(
    location.state as PurchaseRequestFlowState,
  );
  const {
    purchaseRequestData,
    purchaseRequestPreviewReq,
    loadPurchaseRequestData,
  } = usePurchaseRequestPreview(purchaseRequestFlowState);
  const { options, purchaseRequestId } = useTradeSalesStore();
  const amendTradeSaleMutation = useAmendTradeSaleMutation();
  const invalidateQueries = useInvalidateSaleQueries();

  const onBack = useCallback(() => {
    history.push(
      AppRoutes.OrganisationPurchaseRequestSettlement,
      purchaseRequestFlowState,
    );
  }, [history, purchaseRequestFlowState]);

  const onConfirm = useCallback(async () => {
    if (
      !purchaseRequestFlowState?.setup ||
      !purchaseRequestFlowState?.delivery
    ) {
      return;
    }

    if (options.isEditMode) {
      if (!purchaseRequestId) {
        return;
      }

      const resp = await amendTradeSaleMutation.mutateAsync({
        ...purchaseRequestPreviewReq,
        purchaseRequestId,
        advanceCashUponDelivery: ensure(purchaseRequestFlowState.settlement)
          .advanceCashUponDelivery,
        customerPoRef: purchaseRequestFlowState.delivery.customerPoRef ?? null,
        shipmentNumber:
          purchaseRequestFlowState.delivery.shipmentNumber ?? null,
        poFileId: null,
        deliveryInstructionDocs: null,
        settlementMethod: ensure(purchaseRequestFlowState.settlement)
          .settlementOption,
        customDeliveryCost: purchaseRequestFlowState.delivery.customDeliveryCost
          ?.amount
          ? purchaseRequestFlowState.delivery.customDeliveryCost
          : null,
        additionalDeliveryCost: purchaseRequestFlowState.delivery
          .additionalDeliveryCost?.amount
          ? purchaseRequestFlowState.delivery.additionalDeliveryCost
          : null,
      });

      if (resp.kind === "success") {
        history.push(
          `${AppRoutes.OrganisationPurchaseRequestDetails}/${purchaseRequestId}`,
        );
      } else {
        void showAlert({
          severity: "error",
          title: "Error while editing purchase request",
          body: getErrorMessage(resp),
        });
      }
      return;
    }

    if (purchaseRequestPreviewReq) {
      try {
        let poFileId: string | null = null;

        // Upload purchase order file if available
        if (purchaseRequestFlowState.setup.poFile) {
          poFileId = await uploadFile(
            isDevelopment,
            service,
            purchaseRequestFlowState.setup.poFile,
            authToken,
          );
        }

        // Upload delivery instruction docs if available
        let deliveryInstructionDocs;
        if (purchaseRequestFlowState.delivery.deliveryInstructionDocs) {
          deliveryInstructionDocs = await Promise.all(
            purchaseRequestFlowState.delivery.deliveryInstructionDocs.map(
              (file) => uploadFile(isDevelopment, service, file, authToken),
            ),
          );
        }

        const request = {
          ...purchaseRequestPreviewReq,
          advanceCashUponDelivery: ensure(purchaseRequestFlowState.settlement)
            .advanceCashUponDelivery,
          customerPoRef:
            purchaseRequestFlowState.delivery.customerPoRef ?? null,
          shipmentNumber:
            purchaseRequestFlowState.delivery.shipmentNumber ?? null,
          poFileId: poFileId,
          deliveryInstructionDocs: deliveryInstructionDocs ?? null,
          settlementMethod: ensure(purchaseRequestFlowState.settlement)
            .settlementOption,
          customDeliveryCost: purchaseRequestFlowState.delivery
            .customDeliveryCost?.amount
            ? purchaseRequestFlowState.delivery.customDeliveryCost
            : null,
          additionalDeliveryCost: purchaseRequestFlowState.delivery
            .additionalDeliveryCost?.amount
            ? purchaseRequestFlowState.delivery.additionalDeliveryCost
            : null,
          storefrontOrder: false,
          receivableType: "platformOriginated" as const,
        };

        const resp = await service.createPurchaseRequest({
          ...request,
          idempotencyKey: hash(request),
        });

        if (resp.kind === "success") {
          // Invalidate queries after successful creation
          invalidateQueries.invalidateAllQueries();

          loadPurchaseRequestData(resp.value);
          history.push(
            `${AppRoutes.OrganisationPurchaseRequestDetails}/${resp.value}`,
          );
        } else {
          throw new Error(getErrorMessage(resp));
        }
      } catch (error) {
        void showAlert({
          severity: "error",
          title: "Error while creating purchase request",
          body: error instanceof Error ? error.message : String(error),
        });
        loadPurchaseRequestData();
      }
    }
  }, [
    purchaseRequestPreviewReq,
    loadPurchaseRequestData,
    purchaseRequestFlowState,
    service,
    showAlert,
    isDevelopment,
    authToken,
    options.isEditMode,
    purchaseRequestId,
    amendTradeSaleMutation,
    history,
    invalidateQueries,
  ]);

  const onCreateAnother = useCallback(() => {
    const purchaser = purchaseRequestFlowState.setup?.purchaser;
    if (purchaser) {
      const initialFlowState: PurchaseRequestFlowState = {
        setup: {
          bondedSale: true,
          salePriceType: "exDutyAndVat",
          purchaser: { ...purchaser },
          products: [],
          dealDiscountAmount: null,
        },
      };
      history.push(
        AppRoutes.OrganisationCreatePurchaseRequest,
        initialFlowState,
      );
    } else {
      throw new Error("Invalid purchase request flow state");
    }
  }, [history, purchaseRequestFlowState.setup?.purchaser]);

  if (!purchaseRequestFlowState?.setup || !purchaseRequestFlowState?.delivery) {
    return <Redirect to={AppRoutes.Index} />;
  }

  return (
    <Loader loadingStates={[purchaseRequestData]} fullScreen>
      {isLoaded(purchaseRequestData) && (
        <OrganisationPurchaseRequestConfirmationPageView
          purchaseRequestData={purchaseRequestData.value}
          poFileAdded={!!purchaseRequestFlowState.setup.poFile}
          onCreateAnother={onCreateAnother}
          onBack={onBack}
          onConfirm={onConfirm}
        />
      )}
    </Loader>
  );
};
